import { endpointTemplate, statusOf } from './metric-attrs';

/**
 * One failed HTTP call, in bounded form, ready to become an API_ERROR event.
 */
export interface ApiErrorReport {
    /** Path template with ids replaced — never a raw URL. */
    endpoint: string;
    method: string;
    /** HTTP status, or 0 when the request never reached a server. */
    status: number;
    /** Round trip in ms, measured from just before the request was issued. */
    ms: number;
    /** The server's `X-Request-Id`, when the response exposes it (CORS). */
    requestId?: string;
}

/**
 * The seam the HTTP clients report failures through.
 *
 * A static sink rather than a DI edge, and for a harder reason than usual:
 * `MetricsService` depends on `HttpClient`, and transitively (via
 * WebSocketService -> SettingsService) on `LightHttp`. Importing MetricsService
 * from either client would close a real import cycle, not merely a risky one.
 * `MetricsService` registers itself in its own constructor, exactly as
 * HelperService/AuthService/LoginService already do for the header and token
 * providers.
 */
export interface ApiErrorSink {
    reportApiError(report: ApiErrorReport): void;
}

/**
 * The metrics ingest endpoint itself. Excluded from API_ERROR reporting: a
 * failing flush would emit an event describing the failure, which the next
 * flush would try to send to the same broken endpoint, which would emit another.
 * Nothing else is excluded — 401 refresh rounds and enforcement 429s are the
 * product signal we are trying to see, not noise (decision 2026-09-02).
 */
const METRICS_INGEST_PATH = '/metrics/events';

/**
 * Read `X-Request-Id` off a failed response, if the server exposed it.
 *
 * Uses `getAllResponseHeaders()` rather than `getResponseHeader()` on purpose:
 * the latter logs a "Refused to get unsafe header" console error on every
 * cross-origin response for a header that is not CORS-safelisted, and our API is
 * always a different subdomain. getAll…() simply omits what it may not read, so
 * this quietly returns undefined until nginx lists the header in
 * `Access-Control-Expose-Headers` (METRICS.md §7.2).
 */
export function readRequestId(error: any): string | undefined {
    try {
        const xhr = error?.xhr || error?.request || error?.response?.request;
        const all = xhr?.getAllResponseHeaders?.();
        if (typeof all === 'string' && all.length) {
            const match = /^x-request-id:\s*(.+)$/im.exec(all);
            if (match) return match[1].trim();
        }
        const fromHeaders = error?.response?.headers?.['x-request-id'];
        if (typeof fromHeaders === 'string' && fromHeaders.length) return fromHeaders;
    } catch { /* best-effort */ }
    return undefined;
}

/**
 * Build the bounded report for one failed request, or null when this failure
 * must not be reported (no sink wired yet, or the metrics endpoint itself).
 *
 * Emitted once per ATTEMPT: a 401 that triggers a silent refresh-and-replay
 * produces one API_ERROR for the 401 and, if the replay also fails, one more.
 * That is deliberate — the refresh dance failing is itself a thing we want a
 * rate for, and collapsing attempts would hide it (decision 2026-09-02).
 */
export function buildApiErrorReport(
    url: string,
    method: string,
    error: any,
    ms: number,
): ApiErrorReport | null {
    const endpoint = endpointTemplate(url);
    if (endpoint.indexOf(METRICS_INGEST_PATH) !== -1) return null;
    return {
        endpoint,
        method: (method || 'GET').toUpperCase(),
        status: statusOf(error),
        ms,
        ...(readRequestId(error) ? { requestId: readRequestId(error) } : {}),
    };
}

/** Report a failed request through the sink; never throws. */
export function reportApiError(
    sink: ApiErrorSink | null,
    url: string,
    method: string,
    error: any,
    ms: number,
): void {
    if (!sink) return;
    try {
        const report = buildApiErrorReport(url, method, error, ms);
        if (report) sink.reportApiError(report);
    } catch { /* observability is never load-bearing */ }
}
